import {EditorPoint, TopViewWallAreaMeasurement, WallDeduction} from "../../models/editor";
import {formatArea} from "../../../../../../utils/TextUtils";
import {UnitsType} from "../../../../../../models/enums";

export class TopViewWallAreaHelper {
	/**
	 * Calculates the wall area of a single line segment using the trapezoidal formula:
	 * area = lineDistance × ((height1 + height2) / 2)
	 */
	static calculateSegmentLineArea(lineDistance: number, height1: number, height2: number): number {
		return lineDistance * ((height1 + height2) / 2);
	}

	private static computeGrossArea(measurement: TopViewWallAreaMeasurement, scale: number): number {
		let gross = 0;
		for (const segment of measurement.wallSegments) {
			for (const line of segment.lines) {
				const h1 = measurement.heightMap[line.from.id] ?? 0;
				const h2 = measurement.heightMap[line.to.id] ?? 0;
				gross += TopViewWallAreaHelper.calculateSegmentLineArea(line.distance * scale, h1, h2);
			}
			// Closed segments have a visual closing line (last → first) not stored in lines[]
			if (segment.closed && segment.lines.length > 0) {
				const firstPoint = segment.lines[0].from;
				const lastPoint = segment.lines[segment.lines.length - 1].to;
				const dx = lastPoint.position.x - firstPoint.position.x;
				const dy = lastPoint.position.y - firstPoint.position.y;
				const closingDist = Math.hypot(dx, dy) * scale;
				const h1 = measurement.heightMap[lastPoint.id] ?? 0;
				const h2 = measurement.heightMap[firstPoint.id] ?? 0;
				gross += TopViewWallAreaHelper.calculateSegmentLineArea(closingDist, h1, h2);
			}
		}
		return gross;
	}

	/**
	 * Calculates the total wall area for a measurement by summing all wall segment line areas,
	 * then subtracting all wall deductions. Result is capped at 0 (never negative).
	 * Scale converts pixel distances to real-world units.
	 */
	static calculateTotalArea(measurement: TopViewWallAreaMeasurement, scale: number | null): number {
		if (!scale) return 0;
		const gross = TopViewWallAreaHelper.computeGrossArea(measurement, scale);
		const totalDeductions = measurement.wallDeductions.reduce((sum, d) => sum + d.area, 0);
		return Math.max(0, gross - totalDeductions);
	}

	static calculateGrossArea(measurement: TopViewWallAreaMeasurement, scale: number | null): number {
		if (!scale) return 0;
		return TopViewWallAreaHelper.computeGrossArea(measurement, scale);
	}

	/**
	 * Calculates the area of a trapezoid deduction from its four side lengths.
	 * topWidth and bottomWidth are the parallel edges; leftHeight and rightHeight are the legs.
	 *
	 * Derivation: place bottomWidth at y=0, topWidth at height h with left overhang x.
	 *   leftHeight² = x² + h²
	 *   rightHeight² = (p − x)² + h²   where p = bottomWidth − topWidth
	 *   → x = (leftHeight² − rightHeight² + p²) / (2p)
	 *   → h = sqrt(leftHeight² − x²)
	 *   → area = ((topWidth + bottomWidth) / 2) × h
	 *
	 * When topWidth === bottomWidth the perpendicular height is the average of the two legs.
	 * If the inputs are geometrically inconsistent (h² ≤ 0) it falls back to the
	 * average-dimensions approximation.
	 */
	static calculateDeductionArea(d: Pick<WallDeduction, 'topWidth' | 'bottomWidth' | 'leftHeight' | 'rightHeight'>): number {
		const {topWidth, bottomWidth, leftHeight, rightHeight} = d;
		const p = bottomWidth - topWidth;

		if (Math.abs(p) < 1e-9) {
			return topWidth * ((leftHeight + rightHeight) / 2);
		}

		const x = (leftHeight * leftHeight - rightHeight * rightHeight + p * p) / (2 * p);
		const hSquared = leftHeight * leftHeight - x * x;

		if (hSquared <= 0) {
			return ((topWidth + bottomWidth) / 2) * ((leftHeight + rightHeight) / 2);
		}

		return ((topWidth + bottomWidth) / 2) * Math.sqrt(hSquared);
	}

	/**
	 * Returns an Excel-style label for a zero-based point index.
	 * 0→A, 25→Z, 26→AA, 27→AB, 51→AZ, 52→BA, …
	 */
	static getPointLabel(index: number): string {
		let label = "";
		let n = index + 1;
		while (n > 0) {
			const remainder = (n - 1) % 26;
			label = String.fromCharCode(65 + remainder) + label;
			n = Math.floor((n - 1) / 26);
		}
		return label;
	}

	/**
	 * Returns the unique points across all wall segments in traversal order.
	 * Duplicate point IDs (e.g. the closing point of a closed segment) are skipped.
	 */
	static getOrderedUniquePoints(measurement: TopViewWallAreaMeasurement): EditorPoint[] {
		const seen = new Set<string>();
		const points: EditorPoint[] = [];
		for (const segment of measurement.wallSegments) {
			for (const line of segment.lines) {
				if (!seen.has(line.from.id)) {
					seen.add(line.from.id);
					points.push(line.from);
				}
				if (!seen.has(line.to.id)) {
					seen.add(line.to.id);
					points.push(line.to);
				}
			}
		}
		return points;
	}

	/**
	 * Returns a formatted string of the total wall area.
	 */
	static getFormattedValue(
		measurement: TopViewWallAreaMeasurement,
		scale: number | null,
		unitsType: UnitsType | undefined
	): string {
		return formatArea(TopViewWallAreaHelper.calculateTotalArea(measurement, scale), unitsType);
	}
}
